import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Upload, Save, X } from 'lucide-react'
import axios from 'axios'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ContentCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`

const Section = styled.div`
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 15px 0;
`

const CoverImagePreview = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 12px;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  background-color: #f0f0f0;
  margin-bottom: 20px;
  border: 2px dashed ${props => props.$hasImage ? 'transparent' : props.theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    height: 250px;
  }
`

const PlaceholderText = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 16px;
  text-align: center;
`

const RemoveImageButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #ff4757;
    color: white;
    transform: scale(1.1);
  }
`

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
    border-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

const InfoText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 10px 0 0 0;
  line-height: 1.5;
`

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 15px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBackground || props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const CancelButton = styled.button`
  padding: 12px 24px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: transparent;
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const LoadingSpinner = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;

  h3 {
    margin: 10px 0 0 0;
    color: #333;
  }
`

export default function CoverImageEditPage() {
  const { theme } = useTheme()
  const { isLoggedIn } = useAuth()
  const navigate = useNavigate()
  const { id } = useParams() // 퀴즈 세트 ID

  const [quizData, setQuizData] = useState(null)
  const [coverImage, setCoverImage] = useState('')
  const [originalCoverImage, setOriginalCoverImage] = useState('')
  const [description, setDescription] = useState('')
  const [originalDescription, setOriginalDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  // 퀴즈 데이터 조회
  useEffect(() => {
    if (!isLoggedIn) {
      alert('로그인이 필요합니다.')
      navigate('/login')
      return
    }

    const fetchQuizData = async () => {
      try {
        setLoading(true)
        console.log('📚 퀴즈 데이터 조회:', id)

        const response = await fetch(`/auth/get-quiz/${id}`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()
        console.log('📚 퀴즈 데이터:', data)

        if (data.success && data.quiz) {
          setQuizData(data.quiz)
          setCoverImage(data.quiz.set_cover_img || '')
          setOriginalCoverImage(data.quiz.set_cover_img || '')
          setDescription(data.quiz.set_description || '')
          setOriginalDescription(data.quiz.set_description || '')
        } else {
          throw new Error(data.error || '퀴즈 데이터를 불러오는데 실패했습니다.')
        }
      } catch (error) {
        console.error('❌ 퀴즈 데이터 조회 실패:', error)
        alert('퀴즈 데이터를 불러오는 중 오류가 발생했습니다.')
        navigate('/myset')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchQuizData()
    }
  }, [id, isLoggedIn, navigate])

  // file.skynet.re.kr로 파일 업로드 함수
  const uploadToSkynet = async (file) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const formData = new FormData()
    formData.append('file', file)
    formData.append('uploadId', uploadId)

    try {
      const response = await axios.post('/ObjectUpload/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const uploadedUrl = response.data?.data?.data?.files?.[0]?.url

      if (uploadedUrl) {
        console.log('✅ 파일 업로드 성공:', uploadedUrl)
        return uploadedUrl
      } else {
        throw new Error('업로드된 URL을 받지 못했습니다.')
      }
    } catch (error) {
      console.error('❌ 파일 업로드 실패:', error)
      throw error
    }
  }

  // 이미지 업로드 처리
  const handleImageUpload = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'

    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (file) {
        // 파일 크기 체크 (5MB = 5 * 1024 * 1024 bytes)
        const maxSize = 5 * 1024 * 1024
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)

        if (file.size > maxSize) {
          alert(`이미지 파일이 너무 큽니다.\n현재 크기: ${fileSizeMB}MB\n최대 허용 크기: 5MB\n\n파일 크기를 줄여서 다시 업로드해주세요.`)
          return
        }

        console.log(`📷 커버 이미지 업로드 시작: ${file.name} (${fileSizeMB}MB)`)

        try {
          setUploading(true)

          // file.skynet.re.kr로 업로드
          const uploadedUrl = await uploadToSkynet(file)

          setCoverImage(uploadedUrl)
          console.log(`✅ 커버 이미지 업로드 완료: ${uploadedUrl}`)

          alert('이미지가 업로드되었습니다!')
        } catch (error) {
          alert(`이미지 업로드에 실패했습니다: ${error.message}`)
          console.error('이미지 업로드 오류:', error)
        } finally {
          setUploading(false)
        }
      }
    }

    input.click()
  }

  // 이미지 제거
  const handleRemoveImage = () => {
    setCoverImage('')
  }

  // 변경사항이 있는지 확인
  const hasChanges = coverImage !== originalCoverImage || description !== originalDescription

  // 저장
  const handleSave = async () => {
    if (!hasChanges) {
      alert('변경된 내용이 없습니다.')
      return
    }

    try {
      setLoading(true)
      console.log('💾 커버 이미지/설명 저장 시작:', { id, coverImage, description })

      const response = await fetch(`/auth/update-quiz-cover/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ coverImage, description })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('💾 저장 응답:', data)

      if (data.success) {
        alert('변경사항이 저장되었습니다!')
        navigate('/myset')
      } else {
        throw new Error(data.error || '저장에 실패했습니다.')
      }
    } catch (error) {
      console.error('❌ 저장 실패:', error)
      alert('저장 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  // 취소
  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('변경사항이 저장되지 않았습니다. 정말 나가시겠습니까?')) {
        navigate('/myset')
      }
    } else {
      navigate('/myset')
    }
  }

  if (!isLoggedIn) {
    return null
  }

  return (
    <>
      <Container theme={theme}>
        <Header>
          <BackButton theme={theme} onClick={handleCancel}>
            <ArrowLeft size={20} />
            뒤로 가기
          </BackButton>
          <Title theme={theme}>커버 이미지 변경</Title>
        </Header>

        <ContentCard theme={theme}>
          <Section>
            <SectionTitle theme={theme}>퀴즈 세트 정보</SectionTitle>
            {quizData && (
              <InfoText theme={theme}>
                <strong>{quizData.set_title}</strong>
              </InfoText>
            )}
          </Section>

          <Section>
            <SectionTitle theme={theme}>퀴즈 세트 설명</SectionTitle>
            <DescriptionTextarea
              theme={theme}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="퀴즈 세트에 대한 설명을 입력해주세요..."
              maxLength={500}
            />
            <InfoText theme={theme}>
              • 최대 500자까지 입력 가능합니다.
            </InfoText>
          </Section>

          <Section>
            <SectionTitle theme={theme}>커버 이미지</SectionTitle>
            <CoverImagePreview
              theme={theme}
              $image={coverImage}
              $hasImage={!!coverImage}
            >
              {!coverImage && (
                <PlaceholderText theme={theme}>
                  커버 이미지를 업로드해주세요
                </PlaceholderText>
              )}
              {coverImage && (
                <RemoveImageButton onClick={handleRemoveImage}>
                  <X size={20} />
                </RemoveImageButton>
              )}
            </CoverImagePreview>

            <UploadButton
              theme={theme}
              onClick={handleImageUpload}
              disabled={uploading}
            >
              <Upload size={16} />
              {uploading ? '업로드 중...' : '이미지 업로드'}
            </UploadButton>

            <InfoText theme={theme}>
              • 권장 크기: 1200 x 630px (16:9 비율)<br />
              • 최대 파일 크기: 5MB<br />
              • 지원 형식: JPG, PNG, GIF
            </InfoText>
          </Section>
        </ContentCard>

        <ButtonGroup>
          <CancelButton theme={theme} onClick={handleCancel}>
            취소
          </CancelButton>
          <SaveButton
            theme={theme}
            onClick={handleSave}
            disabled={loading || uploading || !hasChanges}
          >
            <Save size={16} />
            저장
          </SaveButton>
        </ButtonGroup>
      </Container>

      {(loading || uploading) && (
        <LoadingOverlay>
          <LoadingSpinner>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
            <h3>{uploading ? '업로드 중...' : '저장 중...'}</h3>
          </LoadingSpinner>
        </LoadingOverlay>
      )}
    </>
  )
}
